/**
 * Custom Role Management Component
 * 
 * Allows Enterprise organization admins to create, edit, and manage custom roles
 * 
 * @module CustomRoleManagement
 */

import React, { useState, useEffect } from 'react';
import { customRoleService, CustomRole } from '@/services/customRoleService';
import { permissionService, RESOURCES, ACTIONS, Permission } from '@/services/permissionService';
import { useAuth } from '@/contexts/AuthContext';
import { isAdminInOrganization } from '@/utils/roleHelpers';
import { isEnterprisePlan } from '@/utils/enterpriseSecurity';

interface CustomRoleManagementProps {
  organizationId: string;
}

const CustomRoleManagement: React.FC<CustomRoleManagementProps> = ({ organizationId }) => {
  const { user } = useAuth();
  const [roles, setRoles] = useState<CustomRole[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEnterprise, setIsEnterprise] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRole, setEditingRole] = useState<CustomRole | null>(null);

  // Check if Enterprise and Admin
  useEffect(() => {
    const checkAccess = async () => {
      if (!user || !organizationId) return;
      
      const enterprise = await isEnterprisePlan(organizationId);
      const admin = isAdminInOrganization(user, organizationId);
      setIsEnterprise(enterprise);
      setIsAdmin(admin);
      
      if (enterprise && admin) {
        loadRoles();
      }
    };
    checkAccess();
  }, [organizationId, user]);

  const loadRoles = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await customRoleService.getCustomRolesByOrganization(organizationId);
      if (result.success && result.roles) {
        setRoles(result.roles.filter(r => r.isActive));
      } else {
        setError(result.error || 'Failed to load roles');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (roleId: string) => {
    if (!confirm('Are you sure you want to delete this role? Users with this role will keep it until manually removed.')) {
      return;
    }

    const result = await customRoleService.deleteCustomRole(roleId, organizationId);
    if (result.success) {
      loadRoles();
    } else {
      alert(result.error || 'Failed to delete role');
    }
  };

  // Don't show if not Enterprise or not Admin
  if (!isEnterprise) {
    return (
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded">
        <p className="text-yellow-800">
          Custom roles are only available for Enterprise customers.
        </p>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded">
        <p className="text-red-800">
          Only organization administrators can manage custom roles.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">Custom Roles</h2>
          <p className="text-gray-600 mt-1">
            Create and manage custom roles with fine-grained permissions for your organization.
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          + Create Custom Role
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded text-red-800">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-8">Loading roles...</div>
      ) : roles.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p className="mb-2">No custom roles created yet.</p>
          <p>Create your first role to get started.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {roles.map((role) => (
            <RoleCard
              key={role.id}
              role={role}
              organizationId={organizationId}
              onEdit={() => setEditingRole(role)}
              onDelete={() => handleDelete(role.id)}
              onUpdate={loadRoles}
            />
          ))}
        </div>
      )}

      {showCreateModal && (
        <RoleModal
          organizationId={organizationId}
          userId={user?.id || ''}
          onClose={() => {
            setShowCreateModal(false);
            loadRoles();
          }}
        />
      )}

      {editingRole && (
        <RoleModal
          organizationId={organizationId}
          userId={user?.id || ''}
          role={editingRole}
          onClose={() => {
            setEditingRole(null);
            loadRoles();
          }}
        />
      )}
    </div>
  );
};

// Role Card Component
interface RoleCardProps {
  role: CustomRole;
  organizationId: string;
  onEdit: () => void;
  onDelete: () => void;
  onUpdate: () => void;
}

const RoleCard: React.FC<RoleCardProps> = ({ role, onEdit, onDelete }) => {
  return (
    <div className="border rounded-lg p-4 bg-white shadow-sm hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <h3 className="text-lg font-semibold">{role.name}</h3>
          <p className="text-gray-600 text-sm mt-1">{role.description}</p>
          <div className="mt-3">
            <p className="text-sm font-medium text-gray-700 mb-2">Permissions:</p>
            <div className="space-y-1">
              {role.permissions.map((perm, idx) => (
                <div key={idx} className="text-sm text-gray-600 bg-gray-50 px-2 py-1 rounded">
                  <span className="font-medium">{perm.resource}</span>: {Array.isArray(perm.action) ? perm.action.join(', ') : perm.action}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex gap-2 ml-4">
          <button
            onClick={onEdit}
            className="px-3 py-1 text-sm bg-gray-100 rounded hover:bg-gray-200"
          >
            Edit
          </button>
          <button
            onClick={onDelete}
            className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

// Role Modal Component (Create/Edit)
interface RoleModalProps {
  organizationId: string;
  userId: string;
  role?: CustomRole; // If provided, edit mode; otherwise, create mode
  onClose: () => void;
}

const RoleModal: React.FC<RoleModalProps> = ({ organizationId, userId, role, onClose }) => {
  const [name, setName] = useState(role?.name || '');
  const [description, setDescription] = useState(role?.description || '');
  const [permissions, setPermissions] = useState<Permission[]>(role?.permissions || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (role) {
        // Update existing role
        const result = await customRoleService.updateCustomRole(role.id, {
          name,
          description,
          permissions,
        }, organizationId);

        if (result.success) {
          onClose();
        } else {
          setError(result.error || 'Failed to update role');
        }
      } else {
        // Create new role
        const result = await customRoleService.createCustomRole({
          name,
          description,
          organizationId,
          createdBy: userId,
          permissions,
        });

        if (result.success) {
          onClose();
        } else {
          setError(result.error || 'Failed to create role');
        }
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const addPermission = () => {
    setPermissions([
      ...permissions,
      {
        resource: RESOURCES.TASKS,
        action: ACTIONS.READ,
        conditions: { organizationId: 'self' },
      },
    ]);
  };

  const removePermission = (index: number) => {
    setPermissions(permissions.filter((_, i) => i !== index));
  };

  const updatePermission = (index: number, field: string, value: any) => {
    const updated = [...permissions];
    updated[index] = { ...updated[index], [field]: value };
    setPermissions(updated);
  };

  const resourceOptions = Object.values(RESOURCES);
  const actionOptions = Object.values(ACTIONS);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold mb-4">
          {role ? 'Edit Custom Role' : 'Create Custom Role'}
        </h2>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-800">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              placeholder="e.g., Compliance Officer"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description *
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              required
              placeholder="Describe the role's responsibilities..."
            />
          </div>

          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Permissions *
              </label>
              <button
                type="button"
                onClick={addPermission}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                + Add Permission
              </button>
            </div>

            {permissions.length === 0 ? (
              <p className="text-sm text-gray-500 py-4 text-center border border-dashed rounded">
                No permissions added yet. Click "Add Permission" to get started.
              </p>
            ) : (
              <div className="space-y-3">
                {permissions.map((perm, idx) => (
                  <div key={idx} className="border rounded p-3 bg-gray-50">
                    <div className="grid grid-cols-2 gap-3 mb-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Resource
                        </label>
                        <select
                          value={perm.resource}
                          onChange={(e) => updatePermission(idx, 'resource', e.target.value)}
                          className="w-full px-2 py-1 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {resourceOptions.map((res) => (
                            <option key={res} value={res}>
                              {res}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Actions (comma-separated)
                        </label>
                        <input
                          type="text"
                          value={Array.isArray(perm.action) ? perm.action.join(', ') : perm.action}
                          onChange={(e) => {
                            const actions = e.target.value.split(',').map(a => a.trim()).filter(Boolean);
                            updatePermission(idx, 'action', actions.length === 1 ? actions[0] : actions);
                          }}
                          className="w-full px-2 py-1 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="read, create, update"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Available: {actionOptions.slice(0, 5).join(', ')}...
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removePermission(idx)}
                      className="text-xs text-red-600 hover:text-red-800 font-medium"
                    >
                      Remove Permission
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !name || !description || permissions.length === 0}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (role ? 'Updating...' : 'Creating...') : (role ? 'Update Role' : 'Create Role')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomRoleManagement;

